// スタッフ情報バリデーション
// 入力値の検証とエラーメッセージ生成

import { StaffInfo, StaffBasicInfo, StaffValidationResult, WorkingHours, BreakTime } from '../types/staff';

// メールアドレスの正規表現
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 電話番号の正規表現（日本の形式）
const PHONE_REGEX = /^0\d{1,4}-?\d{1,4}-?\d{3,4}$|^0\d{9,11}$/;

// 時間形式の正規表現 (HH:mm)
const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

// 基本情報のバリデーション
export const validateBasicInfo = (basicInfo: StaffBasicInfo): StaffValidationResult => {
  const errors: { field: string; message: string }[] = [];

  // 名前の検証
  if (!basicInfo.firstName.trim()) {
    errors.push({ field: 'firstName', message: '名前を入力してください' });
  } else if (basicInfo.firstName.length > 20) {
    errors.push({ field: 'firstName', message: '名前は20文字以内で入力してください' });
  }

  if (!basicInfo.lastName.trim()) {
    errors.push({ field: 'lastName', message: '姓を入力してください' });
  } else if (basicInfo.lastName.length > 20) {
    errors.push({ field: 'lastName', message: '姓は20文字以内で入力してください' });
  }

  // メールアドレスの検証
  if (!basicInfo.email.trim()) {
    errors.push({ field: 'email', message: 'メールアドレスを入力してください' });
  } else if (!EMAIL_REGEX.test(basicInfo.email)) {
    errors.push({ field: 'email', message: '有効なメールアドレスを入力してください' });
  }

  // 電話番号の検証
  if (!basicInfo.phone.trim()) {
    errors.push({ field: 'phone', message: '電話番号を入力してください' });
  } else if (!PHONE_REGEX.test(basicInfo.phone.replace(/-/g, ''))) {
    errors.push({ field: 'phone', message: '有効な電話番号を入力してください（例: 090-1234-5678）' });
  }

  // 入社日の検証
  const today = new Date();
  const hireDate = new Date(basicInfo.hireDate);
  
  if (isNaN(hireDate.getTime())) {
    errors.push({ field: 'hireDate', message: '有効な入社日を入力してください' });
  } else if (hireDate > today) {
    errors.push({ field: 'hireDate', message: '入社日は今日より前の日付を入力してください' });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// 勤務時間のバリデーション
export const validateWorkingHours = (workingHours: WorkingHours): StaffValidationResult => {
  const errors: { field: string; message: string }[] = [];

  // 時間形式の検証
  if (!TIME_REGEX.test(workingHours.start)) {
    errors.push({ field: 'workingHours.start', message: '開始時間は HH:mm 形式で入力してください' });
  }

  if (!TIME_REGEX.test(workingHours.end)) {
    errors.push({ field: 'workingHours.end', message: '終了時間は HH:mm 形式で入力してください' });
  }

  // 開始時間と終了時間の関係性チェック
  if (TIME_REGEX.test(workingHours.start) && TIME_REGEX.test(workingHours.end)) {
    const start = timeStringToMinutes(workingHours.start);
    const end = timeStringToMinutes(workingHours.end);

    if (start >= end) {
      errors.push({ field: 'workingHours', message: '開始時間は終了時間より前にしてください' });
    }

    // 勤務時間が24時間を超えないようにチェック
    const workDuration = end - start;
    if (workDuration > 24 * 60) {
      errors.push({ field: 'workingHours', message: '勤務時間は24時間以内にしてください' });
    }

    // 最低勤務時間のチェック（30分以上）
    if (workDuration < 30) {
      errors.push({ field: 'workingHours', message: '勤務時間は30分以上にしてください' });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// 休憩時間のバリデーション
export const validateBreakTime = (
  breakTime: BreakTime,
  workingHours: WorkingHours
): StaffValidationResult => {
  const errors: { field: string; message: string }[] = [];

  // 時間形式の検証
  if (!TIME_REGEX.test(breakTime.start)) {
    errors.push({ field: 'breakTime.start', message: '休憩開始時間は HH:mm 形式で入力してください' });
  }

  if (!TIME_REGEX.test(breakTime.end)) {
    errors.push({ field: 'breakTime.end', message: '休憩終了時間は HH:mm 形式で入力してください' });
  }

  // 休憩時間の関係性チェック
  if (TIME_REGEX.test(breakTime.start) && TIME_REGEX.test(breakTime.end)) {
    const breakStart = timeStringToMinutes(breakTime.start);
    const breakEnd = timeStringToMinutes(breakTime.end);

    if (breakStart >= breakEnd) {
      errors.push({ field: 'breakTime', message: '休憩開始時間は終了時間より前にしてください' });
    }

    // 休憩時間が勤務時間内にあるかチェック
    if (TIME_REGEX.test(workingHours.start) && TIME_REGEX.test(workingHours.end)) {
      const workStart = timeStringToMinutes(workingHours.start);
      const workEnd = timeStringToMinutes(workingHours.end);

      if (breakStart < workStart || breakEnd > workEnd) {
        errors.push({ field: 'breakTime', message: '休憩時間は勤務時間内にしてください' });
      }
    }

    // 休憩時間の長さチェック（最低15分、最大3時間）
    const breakDuration = breakEnd - breakStart;
    if (breakDuration < 15) {
      errors.push({ field: 'breakTime', message: '休憩時間は15分以上にしてください' });
    } else if (breakDuration > 180) {
      errors.push({ field: 'breakTime', message: '休憩時間は3時間以内にしてください' });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// スタッフ情報全体のバリデーション
export const validateStaffInfo = (staffInfo: Partial<StaffInfo>): StaffValidationResult => {
  const allErrors: { field: string; message: string }[] = [];

  // 基本情報の検証
  if (staffInfo.basicInfo) {
    const basicInfoResult = validateBasicInfo(staffInfo.basicInfo);
    allErrors.push(...basicInfoResult.errors);
  } else {
    allErrors.push({ field: 'basicInfo', message: '基本情報は必須です' });
  }

  // 勤務時間の検証
  if (staffInfo.workSchedule?.workingHours) {
    const workingHoursResult = validateWorkingHours(staffInfo.workSchedule.workingHours);
    allErrors.push(...workingHoursResult.errors);

    // 休憩時間の検証
    if (staffInfo.workSchedule.breakTime) {
      const breakTimeResult = validateBreakTime(
        staffInfo.workSchedule.breakTime,
        staffInfo.workSchedule.workingHours
      );
      allErrors.push(...breakTimeResult.errors);
    }
  }

  // 勤務可能日の検証
  if (staffInfo.workSchedule?.availableDays) {
    if (staffInfo.workSchedule.availableDays.length === 0) {
      allErrors.push({ field: 'availableDays', message: '勤務可能日を最低1日選択してください' });
    }
  }

  // 最大顧客数の検証
  if (staffInfo.preferences?.maxClientsPerDay !== undefined) {
    if (staffInfo.preferences.maxClientsPerDay < 1) {
      allErrors.push({ field: 'maxClientsPerDay', message: '1日最大顧客数は1以上にしてください' });
    } else if (staffInfo.preferences.maxClientsPerDay > 20) {
      allErrors.push({ field: 'maxClientsPerDay', message: '1日最大顧客数は20以下にしてください' });
    }
  }

  // 経験年数の検証
  if (staffInfo.qualifications?.experience !== undefined) {
    if (staffInfo.qualifications.experience < 0) {
      allErrors.push({ field: 'experience', message: '経験年数は0以上にしてください' });
    } else if (staffInfo.qualifications.experience > 50) {
      allErrors.push({ field: 'experience', message: '経験年数は50年以下にしてください' });
    }
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
};

// 時間文字列を分数に変換するヘルパー関数
const timeStringToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

// 分数を時間文字列に変換するヘルパー関数
export const minutesToTimeString = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// 勤務時間の重複チェック
export const checkTimeOverlap = (
  time1Start: string,
  time1End: string,
  time2Start: string,
  time2End: string
): boolean => {
  const start1 = timeStringToMinutes(time1Start);
  const end1 = timeStringToMinutes(time1End);
  const start2 = timeStringToMinutes(time2Start);
  const end2 = timeStringToMinutes(time2End);

  return start1 < end2 && start2 < end1;
};

// XSS対策：文字列のサニタイゼーション
export const sanitizeString = (input: string): string => {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// 入力値の正規化
export const normalizeStaffInput = (staffInfo: Partial<StaffInfo>): Partial<StaffInfo> => {
  const normalized = { ...staffInfo };

  if (normalized.basicInfo) {
    normalized.basicInfo = {
      ...normalized.basicInfo,
      firstName: sanitizeString(normalized.basicInfo.firstName?.trim() || ''),
      lastName: sanitizeString(normalized.basicInfo.lastName?.trim() || ''),
      email: normalized.basicInfo.email?.trim().toLowerCase() || '',
      phone: normalized.basicInfo.phone?.replace(/[^\d-]/g, '') || '',
    };
  }

  if (normalized.preferences?.notes) {
    normalized.preferences.notes = sanitizeString(normalized.preferences.notes.trim());
  }

  return normalized;
};